import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import ListItem from "@insite/mobius/Lists/ListItem";
import UnorderedList from "@insite/mobius/Lists/UnorderedList";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    sections = "sections",
    title = "title",
}

interface SectionModel {
    fields: {
        description: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sections]: SectionModel[];
        [fields.title]: string;
    };
}

type Props = OwnProps;

export interface InfoCardStyles {
    Title?: InjectableCss;
    infoCardContainer?: GridContainerProps;
    infoCardDiv?: GridItemProps;
    description?: InjectableCss;
    main?: InjectableCss;
}

export const infoCardStyles: InfoCardStyles = {
    infoCardContainer: {
        css: css`
            margin: 0 auto;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            width: calc(100% + -100px);

            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [css``, css``, css``])}
        `,
    },
    Title: {
        css: css`
            font-size: 20.8px;
            font-family: "Roboto-Bold";
            text-align: center;
            padding-bottom: 40px;
            text-transform: capitalize;
            color: ${getColor("text.accent")};
            margin-bottom: 16px;
            width: 98%;
            margin: 0 auto !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 14px;
                        padding-bottom: 14px;
                        font-size: 20.8px;
                        text-align: center;
                        margin-top: 0;
                        text-transform: capitalize;
                    `,
                    css`
                        margin-bottom: 14px;
                        padding-bottom: 14px;
                        font-size: 20.8px;
                        text-align: center;
                        margin-top: 0;
                        text-transform: capitalize;
                    `,
                    css`
                        margin-bottom: 14px;
                        padding-bottom: 14px;
                        font-size: 20.8px;
                        text-align: center;
                        margin-top: 0;
                        text-transform: capitalize;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    infoCardDiv: {
        width: [12, 12, 12, 10, 3],
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            height: fit-content;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 9px 0px;
                        flex-basis: 100% !important;
                        max-width: 100% !important;
                    `,
                    css`
                        padding: 9px 0px;
                        flex-basis: 100% !important;
                        max-width: 100% !important;
                    `,
                    css`
                        padding: 9px 0px;
                        flex-basis: 100% !important;
                        max-width: 24% !important;
                    `,
                    css`
                        padding: 9px 0px;
                        flex-basis: 100% !important;
                        max-width: 24% !important;
                    `,
                    null,
                    null,
                ])}

            ul {
                li {
                    margin: 14px 0;
                    font-size: 14.4px;
                    font-family: "Roboto-medium";
                    color: ${getColor("text.main")};
                }
            }
        `,
    },

    description: {
        css: css`
            text-transform: capitalize;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: -webkit-fill-available;
                    `,
                    css`
                        width: -webkit-fill-available;
                    `,
                    css`
                        width: -webkit-fill-available;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    main: {
        css: css`
            padding-right: 0%;
            padding-bottom: 100px;

            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [css``, css``, css``, null, null])}
        `,
    },
};

const styles = infoCardStyles;

const TableData: React.FC<Props> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.main}>
            <StyledWrapper {...styles.Title}>{fields.title}</StyledWrapper>

            <GridContainer {...styles.infoCardContainer}>
                {fields.sections.map((section, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <GridItem key={index} {...styles.infoCardDiv}>
                        <StyledWrapper {...styles.description}>
                            {parse(section.fields.description, parserOptions)}
                        </StyledWrapper>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: TableData,
    definition: {
        group: "Basic",
        displayName: "SE Table List Widget",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Tittle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
                isRequired: true,
                sortOrder: 0,
            },
            {
                name: fields.sections,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                getDisplay: (item: HasFields) => {
                    return item.fields.description;
                },
                fieldDefinitions: [
                    {
                        name: "description",
                        editorTemplate: "RichTextField",
                        displayName: "Description",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
